import React from "react";
import { useNavigate } from "react-router";
import {
  Stack,
  Button,
  Heading1,
  Card,
  theme,
  Heading3,
} from "@ovotech/element";
import { Page, SplitHeading, TextField } from "_core/components";
import { FormApi, Formy } from "_core/form";
import { FormValues, getPostcodeValidator } from "_core/validators/Postcode";
import { ErrorTypes, ProductUrls } from "_core/types";
import { useStore } from "_core/store";
import Mixpanel from "_core/analytics/Mixpanel";
import {
  AnalyticsEvents,
  DataLayerEvent,
} from "_core/analytics/AnalyticsEvents";
import { invalidPostcodeRedirectURL } from "./constants";
import { DataLayer } from "_core/analytics/DataLayer";
import { isPostcodeSupported } from "_core/utils";
import { useFeature } from "_core/utils/optimizely";

const PostcodePageContent = () => {
  const navigate = useNavigate();
  const { postcode, setPostcode, charger } = useStore();
  const [testOneEnabled] = useFeature("ev_charger_test_1");
  const [maintenanceModeEnabled] = useFeature("ev_charger_maintenance_mode");

  if (maintenanceModeEnabled) {
    navigate(ProductUrls.ErrorPage, {
      state: { error: ErrorTypes.MAINTENANCE },
    });
    return <></>;
  }

  const handleSubmit = async (values: FormValues) => {
    const isSupported = isPostcodeSupported(values.postcode);

    Mixpanel.track(AnalyticsEvents.CLICKEDBUTTON, {
      buttonId: "postcode-submit",
      pageName: ProductUrls.Postcode,
      charger,
      isSupported,
    });
    DataLayer.push({
      event: DataLayerEvent.SubmitPostcode,
      pageName: ProductUrls.Postcode,
      charger,
      isSupported,
    });

    if (!isSupported) {
      setTimeout(() => {
        window.location.href = invalidPostcodeRedirectURL;
      }, 250);
    } else {
      setPostcode(values.postcode);
      sessionStorage.setItem("postcode", values.postcode);
      navigate(ProductUrls.OffStreetParking);
    }
  };

  return (
    <>
      <Formy
        enableReinitialize
        initialValues={{ postcode: postcode ?? "" }}
        validationSchema={getPostcodeValidator}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, values }: FormApi<FormValues>) => {
          return (
            <>
              <form
                data-testid="postcode-lookup"
                onSubmit={handleSubmit}
                noValidate
              >
                {testOneEnabled ? (
                  <Heading1>Test 1 is enabled!!!</Heading1>
                ) : null}
                <Stack spaceBetween={10}>
                  <Card className={"ContainerCard"}>
                    <Heading3 className={"SurfaceHeading"}>
                      What’s your postcode?
                    </Heading3>
                    <TextField
                      required
                      iconLeft={"address"}
                      label="We’ll use this to find you a local engineer."
                      placeholder="Enter postcode"
                      id="postcode"
                      name="postcode"
                      value={values.postcode}
                    />
                  </Card>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "right",
                      width: "100%",
                      marginBlock: theme.space["200"],
                    }}
                  >
                    <Button
                      type="submit"
                      iconRight="chevron-right"
                      data-testid="postcode-submit-button"
                    >
                      Next
                    </Button>
                  </div>
                </Stack>
              </form>
            </>
          );
        }}
      </Formy>
    </>
  );
};

export const PostcodePage = () => {
  return (
    <Page
      displaySteps
      title={<SplitHeading part1="Get a" part2="home EV charger" />}
      subtitle=""
      url={ProductUrls.Postcode}
    >
      <PostcodePageContent />
    </Page>
  );
};
